import React, { useState, useEffect } from 'react';
import {

  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  MenuList,
  Paper,
  ClickAwayListener,
  Popper,
  Grow,
  Icon
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  ContextMenuItem, 
  ContextMenuItemStyle, 
  ContextMenuPosition, 
  ContextMenuContext,
  ContextMenuItemKey
} from '../../types/context-menu';

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: ContextMenuPosition | null;
  context: ContextMenuContext | null;
  onClose: () => void;
  onItemClick: (key: ContextMenuItemKey, subItemId?: ContextMenuItemKey) => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  position,
  context,
  onClose,
  onItemClick
}) => {
  const [subMenuState, setSubMenuState] = useState<{
    open: boolean;
    anchorEl: HTMLElement | null;
    items: ContextMenuItem | null;
  }>({
    open: false,
    anchorEl: null,
    items: null
  });

  useEffect(() => {
    if (!position) {
      setSubMenuState({
        open: false,
        anchorEl: null,
        items: null
      });
    }
  }, [position]);

  const handleItemClick = (item: ContextMenuItem, event?: React.MouseEvent) => {
    if (item.disabled) return;

    if (item.isDropdown && item.items.length > 0) {
      // Open submenu
      setSubMenuState({
        open: true,
        anchorEl: event?.currentTarget as HTMLElement || null,
        items: item
      });
    } else {
      // Execute action
      onItemClick(item.key);
      onClose();
    }
  };

  const handleSubItemClick = (parentItem: ContextMenuItem, subItemId: ContextMenuItemKey) => {
    onItemClick(parentItem.key, subItemId);
    onClose();
  };

  const handleSubMenuClose = () => {
    setSubMenuState({
      open: false,
      anchorEl: null,
      items: null
    });
  };

  const getItemStyle = (style: ContextMenuItemStyle) => {
    switch (style) {
      case ContextMenuItemStyle.success:
        return {
          color: '#2e7d32',
          '&:hover': {
            backgroundColor: alpha('#4caf50', 0.1)
          }
        };
      case ContextMenuItemStyle.danger:
        return {
          color: '#d32f2f',
          '&:hover': {
            backgroundColor: alpha('#f44336', 0.1)
          }
        };
      default:
        return {};
    }
  };

  const renderMenuItems = () => {
    return items.map((item) => (
      <React.Fragment key={item.key}>
        <MenuItem
          onClick={(event) => handleItemClick(item, event)}
          disabled={item.disabled}
          sx={{
            ...getItemStyle(item.style),
            fontSize: '0.875rem',
            minHeight: 36,
            paddingY: 1,
            paddingX: 2
          }}
        >
          {item.icon && (
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Icon fontSize="small">{item.icon}</Icon>
            </ListItemIcon>
          )}
          <ListItemText
            primary={item.displayText}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: item.style === ContextMenuItemStyle.danger ? 500 : 400
            }}
          />
          {item.isDropdown && (
            <Icon fontSize="small" sx={{ marginLeft: 1 }}>
              chevron_right
            </Icon>
          )}
        </MenuItem>
        {item.divider && <Divider />}
      </React.Fragment>
    ));
  };

  if (!position || !context || items.length === 0) {
    return null;
  }

  return (
    <>
      {/* Main Context Menu */}
      <Popper
        open={!!position}
        anchorEl={null}
        placement="bottom-start"
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 9999
        }}
        transition
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={150}>
            <Paper
              elevation={8}
              sx={{
                minWidth: 180,
                maxWidth: 280,
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 1
              }}
            >
              <ClickAwayListener onClickAway={onClose}>
                <MenuList dense>
                  {renderMenuItems()}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      {/* Submenu */}
      {subMenuState.open && subMenuState.anchorEl && subMenuState.items && (
        <Popper
          open={subMenuState.open}
          anchorEl={subMenuState.anchorEl}
          placement="right-start"
          style={{ zIndex: 10000 }}
          transition
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={150}>
              <Paper
                elevation={8}
                sx={{
                  minWidth: 160,
                  maxWidth: 240,
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <ClickAwayListener onClickAway={handleSubMenuClose}>
                  <MenuList dense>
                    {subMenuState.items?.items.map((subItem) => (
                      <MenuItem
                        key={subItem.id}
                        onClick={() => handleSubItemClick(subMenuState.items!, subItem.id)}
                        sx={{
                          fontSize: '0.875rem',
                          minHeight: 32,
                          paddingY: 0.5,
                          paddingX: 2
                        }}
                      >
                        <ListItemText
                          primary={subItem.displayText}
                          primaryTypographyProps={{
                            fontSize: '0.875rem'
                          }}
                        />
                      </MenuItem>
                    ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </>
  );
};